import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useIsSubscribed } from '../../hooks/useIsSubscribed';
import { trackFeatureUsed, trackCTAClick } from '../../services/analyticsService';
import { useTranslation } from '../../hooks/useTranslation';
import { translatePlans } from '../../utils/translatePlans';
import { getParsedCreditReports } from '../../services/creditReportService';
import { useScope } from '../../hooks/useScope';
import plans from './plans/plans.js';
import AssessmentForm from './AssessmentForm';
import CustomGoalForm from './CustomGoalForm';
import ReportBasedAssessmentForm from './ReportBasedAssessmentForm';
import './ActionablePlans.css';

const ActionablePlans = () => {
  const history = useHistory();
  const { isSubscribed, loading: subscriptionLoading } = useIsSubscribed();
  const t = useTranslation();
  const { isManaged, isClientView, effectiveUserId } = useScope();
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedFormData, setSelectedFormData] = useState(null);
  const [showCustomGoalForm, setShowCustomGoalForm] = useState(false);
  
  // Report-based assessment state
  const [showReportBasedForm, setShowReportBasedForm] = useState(false);
  const [hasParsedReports, setHasParsedReports] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(true);

  // Determine scope parameters
  const isMentorMasquerade = isManaged && !isClientView;
  const clientUserId = isMentorMasquerade ? effectiveUserId : null;

  // Check for parsed credit reports on mount
  const checkParsedReports = useCallback(async () => {
    try {
      setReportsLoading(true);
      const result = await getParsedCreditReports({ clientUserId });
      setHasParsedReports(result.reports && result.reports.length > 0);
    } catch (err) {
      console.error('Error checking for parsed reports:', err);
      setHasParsedReports(false);
    } finally {
      setReportsLoading(false);
    }
  }, [clientUserId]);

  useEffect(() => {
    checkParsedReports();
  }, [checkParsedReports]);

  // Translate plans data
  const translatedPlans = useMemo(() => translatePlans(plans, t), [t]);

  // Get all category names for expand/collapse functionality
  const getAllCategories = () => {
    return Object.keys(translatedPlans.high_level_goals).filter(category => category !== 'quantifiable_success_targets');
  };

  // Expand all sections
  const expandAll = () => {
    const categories = getAllCategories();
    const expandedState = {};
    categories.forEach(category => {
      expandedState[category] = true;
    });
    setExpandedSections(expandedState);
    
    // Track expand all
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'expand_all',
      action: 'expand',
      source: 'button'
    });
  };

  // Collapse all sections
  const collapseAll = () => {
    setExpandedSections({});
    
    // Track collapse all
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'collapse_all',
      action: 'collapse',
      source: 'button'
    });
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    const wasExpanded = !!expandedSections[sectionId];
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
    
    // Track section toggle
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'category_toggle',
      action: wasExpanded ? 'collapse' : 'expand',
      source: 'category_header',
      additionalParams: {
        category: sectionId
      }
    });
  };

  const generatePlan = (goal, subcategory) => {
    // Check if user is subscribed before allowing action
    if (subscriptionLoading) {
      // Still loading subscription status, wait
      return;
    }
    
    if (!isSubscribed) {
      // Track subscription required
      trackCTAClick({
        section: 'actionable_plans',
        action: 'generate_assessment',
        source: 'button',
        additionalParams: {
          goal_id: goal.id,
          subcategory: subcategory,
          blocked: 'no_subscription'
        }
      });
      
      // Redirect to subscription management page
      history.push('/manage-subscription');
      return;
    }

    // Track generate assessment click
    trackCTAClick({
      section: 'actionable_plans',
      action: 'generate_assessment',
      source: 'button',
      additionalParams: {
        goal_id: goal.id,
        goal_title: goal.goal,
        subcategory: subcategory,
        timeline: goal.timeline
      }
    });

    // User is subscribed, proceed with generating plan
    setSelectedFormData({
      formId: goal.id,
      subcategory: subcategory,
      goal: goal
    });
    setShowForm(true);
  };

  const handleFormClose = () => {
    setSelectedFormData(null);
    setShowForm(false);
  };

  const handleFormSubmit = (submissionData) => {
    console.log('Assessment submitted:', submissionData);
    // Here you can handle the form submission data
    // For example, save to localStorage, send to API, etc.
  };

  const handleCustomGoalSubmit = (submissionData) => {
    console.log('Custom goal submitted:', submissionData);
    // Here you can handle the custom goal submission
    // For example, save to localStorage, send to API, etc.
  };

  const handleNavigateToPlan = (generatedPlan) => {
    console.log('Navigating to generated plan:', generatedPlan);
    
    // Extract planId from generatedPlan object (from generateCreditPlan response)
    const planId = generatedPlan?.planId;
    
    if (planId) {
      // Navigate to credit plan page using URL with planId
      // The component will load the plan from the database using the planId in the URL
      history.push(`/creditplan/${planId}`);
    } else {
      console.warn('No planId found in generatedPlan, falling back to legacy navigation');
      // Fallback to legacy method if planId is not available (backward compatibility)
      localStorage.setItem('generatedPlanData', JSON.stringify(generatedPlan));
      history.push('/creditplan');
    }
  };

  // Flatten all goals from the plans data
  const getAllGoals = () => {
    const goals = [];

    Object.entries(translatedPlans.high_level_goals).forEach(([category, subcategories]) => {
      // Skip quantifiable_success_targets as it's not a goal category
      if (category === 'quantifiable_success_targets') return;

      Object.entries(subcategories).forEach(([subcategory, goalArray]) => {
        // Check if goalArray is actually an array
        if (Array.isArray(goalArray)) {
          goalArray.forEach((goal, index) => {
            goals.push({
              id: `${category}_${subcategory}_${index}`,
              category,
              subcategory,
              ...goal
            });
          });
        }
      });
    });

    return goals;
  };

  // Filter goals by timeframe
  const filterGoalsByTimeframe = (goals) => {
    if (selectedTimeframe === 'all') return goals;

    const timeframeKeywords = {
      'short': ['month', '3-6', '6-12', '12-18', '1_month', '3_month', '6_month'],
      'medium': ['12-24', '24-36', '18-24', '12_month', '18_month', '24_month'],
      'long': ['36+', 'generational', 'long-term', '36_month', '48_month', '60_month', '120_month']
    };

    return goals.filter(goal => {
      const timeline = goal.timeline.toLowerCase();
      const keywords = timeframeKeywords[selectedTimeframe] || [];
      return keywords.some(keyword => timeline.includes(keyword));
    });
  };


  // Render metrics in a readable format
  const renderMetrics = (metrics) => {
    if (!metrics || typeof metrics !== 'object') return null;

    return (
      <div className="metrics-grid">
        {Object.entries(metrics).map(([key, value]) => (
          <div key={key} className="metric-item">
            <span className="metric-label">
              {t(`actionablePlans.metricLabels.${key}`, key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}:
            </span>
            <span className="metric-value">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render success targets in a timeline format
  const renderGoalSuccessTargets = (targets) => {
    if (!targets || typeof targets !== 'object') return null;

    return (
      <div className="targets-section">
        <h4>{t('actionablePlans.successTimeline')}</h4>
        <div className="targets-timeline">
          {Object.entries(targets).map(([key, value]) => (
            <div key={key} className="target-item">
              <span className="target-period">
                {t(`actionablePlans.targetLabels.${key}`, key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}:
              </span>
              <span className="target-value">{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render individual goal card
  const renderGoalCard = (goal, subcategory) => {

    return (
      <div
        key={goal.id}
        className={`goal-card`}
      >
        <div className="goal-header">
          <div className="goal-content">
            <h3 className="goal-title">{goal.goal}</h3>
            <p className="goal-timeline">⏰ {goal.timeline}</p>
          </div>
        </div>

        <div className="goal-details">
          {goal.quantifiable_success_metrics && (
            <div className="metrics-section">
              <h4>{t('actionablePlans.successMetrics')}</h4>
              {renderMetrics(goal.quantifiable_success_metrics)}
            </div>
          )}
          {renderGoalSuccessTargets(goal.quantifiable_success_targets)}
          
          <div className="goal-actions">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                generatePlan(goal, subcategory);
              }}
              className="generate-button"
            >
              {t('actionablePlans.generateAssessment')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render category section
  const renderCategorySection = (category, subcategories) => {
    const categoryGoals = [];

    Object.entries(subcategories).forEach(([subcategory, goals]) => {
      // Check if goals is actually an array
      if (Array.isArray(goals)) {
        goals.forEach((goal, index) => {
          categoryGoals.push({
            id: `${category}_${subcategory}_${index}`,
            category,
            subcategory,
            ...goal
          });
        });
      }
    });

    const filteredGoals = filterGoalsByTimeframe(categoryGoals);
    const sectionId = category;

    return (
      <div key={category} className="category-section">
        <div
          className="category-header"
          onClick={() => toggleSection(sectionId)}
        >
          <div className="category-info">
            <h2 className="category-title">
              {t(`actionablePlans.categories.${category}`, category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
            </h2>
          </div>
          <div className="expand-icon">
            {expandedSections[sectionId] ? '−' : '+'}
          </div>
        </div>

        {expandedSections[sectionId] && (
          <div className="category-content">
            {Object.entries(subcategories).map(([subcategory, goals]) => {
              // Only render if goals is an array
              if (!Array.isArray(goals)) return null;

              return (
                <div key={subcategory} className="subcategory-section">
                  <h3 className="subcategory-title">
                    {t(`actionablePlans.subcategories.${subcategory}`, subcategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
                  </h3>
                  <div className="goals-grid">
                    {goals.map((goal, index) => {
                      const goalId = `${category}_${subcategory}_${index}`;
                      const filteredGoal = filteredGoals.find(g => g.id === goalId);
                      return filteredGoal ? renderGoalCard(filteredGoal, subcategory) : null;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render success targets
  const renderSuccessTargets = () => {
    const targets = translatedPlans.high_level_goals.quantifiable_success_targets;

    // Check if targets exist and is an object
    if (!targets || typeof targets !== 'object') {
      return null;
    }

    return (
      <div className="success-targets-section">
        <h2>{t('actionablePlans.successTargets')}</h2>
        <div className="targets-grid">
          {Object.entries(targets).map(([timeframe, targetData]) => (
            <div key={timeframe} className="target-card">
              <h3 className="target-title">
                {t(`actionablePlans.targetLabels.${timeframe}`, timeframe.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}
              </h3>
              <div className="target-metrics">
                {Object.entries(targetData).map(([metric, value]) => (
                  <div key={metric} className="target-metric">
                    <span className="metric-name">
                      {t(`actionablePlans.metricLabels.${metric}`, metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))}:
                    </span>
                    <span className="metric-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="actionable-plans">
      <div className="plans-header">
        <h1>{t('actionablePlans.title')}</h1>
        <p>{t('actionablePlans.subtitle')}</p>
        
        <div className="expand-controls">
          <button 
            type="button" 
            onClick={expandAll}
            className="expand-all-btn"
          >
            {t('actionablePlans.expandAll')}
          </button>
          <button 
            type="button" 
            onClick={collapseAll}
            className="collapse-all-btn"
          >
            {t('actionablePlans.collapseAll')}
          </button>
          <button 
            type="button" 
            onClick={() => {
              // Check if user is subscribed before allowing custom goal creation
              if (subscriptionLoading) {
                return;
              }
              
              if (!isSubscribed) {
                // Track subscription required
                trackCTAClick({
                  section: 'actionable_plans',
                  action: 'create_custom_goal',
                  source: 'button',
                  additionalParams: {
                    blocked: 'no_subscription'
                  }
                });
                
                // Redirect to subscription management page
                history.push('/manage-subscription');
                return;
              }
              
              // Track custom goal creation click
              trackCTAClick({
                section: 'actionable_plans',
                action: 'create_custom_goal',
                source: 'button'
              });
              
              // User is subscribed, proceed with custom goal form
              setShowCustomGoalForm(true);
            }}
            className="custom-goal-btn"
          >
            {t('actionablePlans.createCustomGoal')}
          </button>
          
          {/* Improve Personal Credit button - only visible when user has parsed reports */}
          {reportsLoading ? (
            <div className="improve-credit-btn-loading">
              <span className="btn-spinner"></span>
              <span>{t('actionablePlans.checkingReports', 'Checking reports...')}</span>
            </div>
          ) : hasParsedReports ? (
            <button 
              type="button" 
              onClick={() => {
                // Check if user is subscribed before allowing report-based assessment
                if (subscriptionLoading) {
                  return;
                }
                
                if (!isSubscribed) {
                  // Track subscription required
                  trackCTAClick({
                    section: 'actionable_plans',
                    action: 'improve_personal_credit',
                    source: 'button',
                    additionalParams: {
                      blocked: 'no_subscription'
                    }
                  });
                  
                  // Redirect to subscription management page
                  history.push('/manage-subscription');
                  return;
                }
                
                // Track improve personal credit click
                trackCTAClick({
                  section: 'actionable_plans',
                  action: 'improve_personal_credit',
                  source: 'button'
                });
                
                // User is subscribed, proceed with report-based assessment form
                setShowReportBasedForm(true);
              }}
              className="improve-credit-btn"
            >
              {t('actionablePlans.improvePersonalCredit', 'Improve Personal Credit')}
            </button>
          ) : null}
        </div>
      </div>

      <div className="plans-content">
        <div className="categories-container">
          {Object.entries(translatedPlans.high_level_goals).map(([category, subcategories]) => {
            if (category === 'quantifiable_success_targets') return null;
            return renderCategorySection(category, subcategories);
          })}
        </div>

        {renderSuccessTargets()}
      </div>

      
      {/* Assessment Form Modal */}
      {showForm && selectedFormData && (
        <AssessmentForm
          formId={selectedFormData.formId}
          subcategory={selectedFormData.subcategory}
          goal={selectedFormData.goal}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}

      {/* Custom Goal Form Modal */}
      {showCustomGoalForm && (
        <CustomGoalForm
          onClose={() => setShowCustomGoalForm(false)}
          onSubmit={handleCustomGoalSubmit}
        />
      )}

      {/* Report-Based Assessment Form Modal */}
      {showReportBasedForm && (
        <ReportBasedAssessmentForm
          onClose={() => setShowReportBasedForm(false)}
          onSubmit={(submissionData) => {
            console.log('Report-based assessment submitted:', submissionData);
          }}
        />
      )}
    </div>
  );
};

export default ActionablePlans; 